import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trash2, BookTemplate, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await adminAPI.templates();
      setTemplates(res.data.templates || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await adminAPI.deleteTemplate(id);
      setTemplates(t => t.filter(x => x.id !== id));
      toast.success('Template deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]" data-testid="admin-templates">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Template Management</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">{templates.length} templates</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-14" />)}</div>
      ) : (
        <>
          {/* Desktop Table */}
          <Card className="hidden sm:block">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Uses</TableHead>
                    <TableHead className="text-xs w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium text-xs sm:text-sm">{t.name}</TableCell>
                      <TableCell className="text-xs font-mono capitalize text-muted-foreground">{t.category}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.status === 'published' ? 'bg-green-500/10 text-green-500' : 'bg-muted text-muted-foreground'}`}>
                          {t.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs font-mono">{t.usage_count || 0}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={() => handleDelete(t.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Mobile Cards */}
          <div className="sm:hidden space-y-2">
            {templates.map((t) => (
              <Card key={t.id}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{t.name}</p>
                      <p className="text-[10px] text-muted-foreground font-mono capitalize">{t.category}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${t.status === 'published' ? 'bg-green-500/10 text-green-500' : 'bg-muted text-muted-foreground'}`}>
                          {t.status}
                        </span>
                        <span className="text-[10px] text-muted-foreground font-mono">{t.usage_count || 0} uses</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => handleDelete(t.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
